// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme (Inspired by EmailLogsManagement)
/**
 * System Overview Component - Design V2
 * Inspiré du design EmailLogsManagement
 *
 * Architecture: Component → Hook (TanStack Query) → Service → API
 * ✅ Utilise useSystemOverview hook avec TanStack Query
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  BarChart3,
  Building,
  Users,
  Search,
  Download,
  Filter,
  RefreshCw,
  TrendingUp,
  Calendar,
  X,
  Info,
} from 'lucide-react';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { useTranslation } from 'react-i18next';
import {
  useSystemOverview,
  formatSystemDate,
  exportSystemStatsToCSV,
  type SystemUser,
  type SystemCompany,
} from '../hooks/useSystemOverview';

const SystemOverview: React.FC = () => {
  const { t } = useTranslation();

  // ============================================================
  // LOCAL STATE (UI only - filters)
  // ============================================================
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'users' | 'companies'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'companies'>('users');
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<SystemCompany | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // ============================================================
  // TANSTACK QUERY HOOK (server data)
  // ============================================================
  const { stats, isLoading, refreshStats } = useSystemOverview();

  // ============================================================
  // V2 DESIGN CLASSES (matching EmailLogsManagement)
  // ============================================================
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none cursor-pointer text-gray-900`;

  const inputClasses = `w-full pl-10 pr-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-gray-900 placeholder-gray-500`;

  // ============================================================
  // COMPUTED VALUES
  // ============================================================
  const filteredUsers = useMemo(() => {
    if (filterType === 'companies') return [];
    return stats.users.filter((user: SystemUser) => {
      return (
        searchTerm === '' ||
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [stats.users, searchTerm, filterType]);

  const filteredCompanies = useMemo(() => {
    if (filterType === 'users') return [];
    return stats.companies.filter((company: SystemCompany) => {
      return (
        searchTerm === '' ||
        company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.siret?.includes(searchTerm)
      );
    });
  }, [stats.companies, searchTerm, filterType]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleExportCSV = useCallback(() => {
    exportSystemStatsToCSV(stats);
  }, [stats]);

  const handleResetFilters = useCallback(() => {
    setSearchTerm('');
    setFilterType('all');
  }, []);

  const handleViewUserDetails = useCallback((user: SystemUser) => {
    setSelectedUser(user);
    setSelectedCompany(null);
    setShowDetailModal(true);
  }, []);

  const handleViewCompanyDetails = useCallback((company: SystemCompany) => {
    setSelectedCompany(company);
    setSelectedUser(null);
    setShowDetailModal(true);
  }, []);

  const getRoleBadge = (role?: string) => {
    const variants: Record<string, 'success' | 'info' | 'warning' | 'alert'> = {
      OWNER: 'success',
      ADMIN: 'info',
      USER: 'pending' as any,
      ACCOUNTANT: 'warning',
    };
    return <Badge variant={variants[role || 'USER'] || 'pending'}>{role || 'USER'}</Badge>;
  };

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (isLoading && stats.totalUsers === 0) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <BarChart3 className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.systemOverview.title')}</h1>
            <p className="text-gray-500 mt-0.5">
              {t('admin.systemOverview.statsSubtitle', {
                version: stats.platformVersion,
                userCount: stats.totalUsers,
                userPlural: stats.totalUsers > 1 ? 's' : '',
                companyCount: stats.totalCompanies,
                companyPlural: stats.totalCompanies > 1 ? 's' : ''
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={refreshStats} variant="ghost" icon={<RefreshCw className="w-4 h-4" />}>
            {t('admin.systemOverview.refresh')}
          </Button>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'secondary' : 'ghost'}
            icon={<Filter className="w-4 h-4" />}
          >
            {t('admin.systemOverview.filters')}
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="secondary"
            disabled={stats.totalUsers === 0 && stats.totalCompanies === 0}
            icon={<Download className="w-4 h-4" />}
          >
            {t('admin.systemOverview.exportCsv')}
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Info className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('admin.systemOverview.dashboardTitle')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('admin.systemOverview.dashboardDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={cardClasses}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.systemOverview.users')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
            <div className="p-2 bg-teal-100 rounded-xl">
              <Users className="w-5 h-5 text-teal-600" />
            </div>
          </div>
        </div>

        <div className={cardClasses}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.systemOverview.companies')}</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.totalCompanies}</p>
            </div>
            <div className="p-2 bg-emerald-100 rounded-xl">
              <Building className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className={cardClasses}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.systemOverview.ratioUS')}</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.totalCompanies > 0
                  ? (stats.totalUsers / stats.totalCompanies).toFixed(1)
                  : '0'}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-xl">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className={cardClasses}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.systemOverview.version')}</p>
              <p className="text-2xl font-bold text-purple-600">{stats.platformVersion}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-xl">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className={cardClasses}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5 text-teal-600" />
              {t('admin.systemOverview.filters')}
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              className="p-2 text-gray-500 hover:bg-white/50 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.systemOverview.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('admin.systemOverview.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.systemOverview.typeLabel')}</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'users' | 'companies')}
                className={selectClasses}
              >
                <option value="all">{t('admin.systemOverview.showAll')}</option>
                <option value="users">{t('admin.systemOverview.usersOnly')}</option>
                <option value="companies">{t('admin.systemOverview.companiesOnly')}</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleResetFilters} variant="ghost" className="flex-1">
                {t('admin.systemOverview.reset')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-white/30">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-300 ${
            activeTab === 'users'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          {t('admin.systemOverview.users')} ({filteredUsers.length})
        </button>
        <button
          onClick={() => setActiveTab('companies')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-300 ${
            activeTab === 'companies'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Building className="w-4 h-4 inline mr-2" />
          {t('admin.systemOverview.companies')} ({filteredCompanies.length})
        </button>
      </div>

      {/* Users Table */}
      {activeTab === 'users' && (
        <div className={`${cardClasses} overflow-hidden p-0`}>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 px-6">
              <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                <Users className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.systemOverview.noUsersFound')}</h3>
              <p className="text-gray-600">{t('admin.systemOverview.noUsersFoundDesc')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/30 border-b border-white/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.name')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.email')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.role')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.creationDate')}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-white/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {user.fullName || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{user.email}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user.role)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">
                            {formatSystemDate(user.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleViewUserDetails(user)}
                          className="px-3 py-1.5 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                        >
                          {t('admin.systemOverview.viewDetails')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Companies Table */}
      {activeTab === 'companies' && (
        <div className={`${cardClasses} overflow-hidden p-0`}>
          {filteredCompanies.length === 0 ? (
            <div className="text-center py-12 px-6">
              <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                <Building className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.systemOverview.noCompaniesFound')}</h3>
              <p className="text-gray-600">{t('admin.systemOverview.noCompaniesFoundDesc')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/30 border-b border-white/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.name')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.siret')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.users')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.creationDate')}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.systemOverview.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filteredCompanies.map((company) => (
                    <tr key={company.id} className="hover:bg-white/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{company.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700 font-mono">
                          {company.siret || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="info">{company.usersCount ?? '-'}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">
                            {formatSystemDate(company.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleViewCompanyDetails(company)}
                          className="px-3 py-1.5 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                        >
                          {t('admin.systemOverview.viewDetails')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (selectedUser || selectedCompany) && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden border border-white/40">
            {/* Header du modal */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedUser ? t('admin.systemOverview.userDetails') : t('admin.systemOverview.companyDetails')}
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white hover:bg-white/20 rounded-xl p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps du modal */}
            <div className="p-6 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              {selectedUser && (
                <>
                  <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                    <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.systemOverview.fullName')}</label>
                    <p className="text-gray-900 font-medium">{selectedUser.fullName || '-'}</p>
                  </div>

                  <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                    <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.systemOverview.email')}</label>
                    <p className="text-gray-900">{selectedUser.email}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.systemOverview.role')}</label>
                      {getRoleBadge(selectedUser.role)}
                    </div>
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.systemOverview.createdAt')}</label>
                      <p className="text-gray-900">{formatSystemDate(selectedUser.createdAt)}</p>
                    </div>
                  </div>

                  {selectedUser.lastLoginAt && (
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('admin.systemOverview.lastLogin')}
                      </label>
                      <p className="text-gray-900">{formatSystemDate(selectedUser.lastLoginAt)}</p>
                    </div>
                  )}
                </>
              )}

              {selectedCompany && (
                <>
                  <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('admin.systemOverview.companyName')}
                    </label>
                    <p className="text-gray-900 font-medium">{selectedCompany.name}</p>
                  </div>

                  <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                    <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.systemOverview.siret')}</label>
                    <p className="text-gray-900 font-mono">{selectedCompany.siret || '-'}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('admin.systemOverview.users')}
                      </label>
                      <Badge variant="info">{selectedCompany.usersCount ?? '-'}</Badge>
                    </div>
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.systemOverview.createdAtF')}</label>
                      <p className="text-gray-900">{formatSystemDate(selectedCompany.createdAt)}</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer du modal */}
            <div className="p-6 border-t border-white/30">
              <Button onClick={() => setShowDetailModal(false)} variant="ghost" className="w-full">
                {t('admin.systemOverview.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { SystemOverview };
export default SystemOverview;
